import { createFileRoute, Link } from "@tanstack/react-router";
import { ctrDesc, ctrTitle } from "@/lib/seo-copy";
import { SITE } from "@/lib/site-config";
import { absoluteAsset, absoluteUrl } from "@/config/sites";
import { canonicalAndHreflang } from "@/lib/hreflang";

import { GlobalImageStrip } from "@/components/GlobalImageStrip";
import { OfficialServiceSources } from "@/components/OfficialServiceSources";
import { FirmHero } from "@/components/firm/FirmHero";
import { TrustBar } from "@/components/firm/TrustBar";
import { PracticeAreaGrid } from "@/components/firm/PracticeAreaGrid";
import { OneStopJourney } from "@/components/firm/OneStopJourney";
import { ProcessSteps, FIRM_STEPS } from "@/components/firm/ProcessSteps";
import { WhyLawFirm } from "@/components/firm/WhyLawFirm";
import { AttorneyStrip } from "@/components/firm/AttorneyStrip";
import { FirmCta } from "@/components/firm/FirmCta";
import firmHeroTeam from "@/assets/firm-hero-team.jpg";
import teamAsset from "@/assets/team-group.png.asset.json";
import {
  buildJsonLd,
  jsonLdScript,
  webPageSchema,
  faqPageSchema,
  legalServiceSchema,
  howToSchema,
} from "@/lib/schema";

const HOME_FAQ = [
  {
    question: "สำนักงานรับงานกฎหมายด้านใดบ้าง นอกจากงานรับรองเอกสาร?",
    answer:
      "สำนักงานเป็นสำนักงานกฎหมายครบวงจร รับงานคดีความและว่าความทุกศาลรวมถึงงานบังคับคดี งานบริษัทและสัญญา ควบรวมกิจการและตรวจสอบสถานะทางกฎหมาย งานครอบครัว มรดกและพันธุกรรม ที่ดินและอสังหาริมทรัพย์ แรงงาน คนต่างชาติและวีซ่า ภาษี ทรัพย์สินทางปัญญา และ PDPA ส่วนงาน Notary Public แปลรับรอง กรมการกงสุลและสถานทูต เป็นหนึ่งในสายงานของสำนักงาน",
  },
  {
    question: "ทนายที่ดูแลเรื่องของเราได้รับใบอนุญาตจริงหรือไม่ ตรวจสอบอย่างไร?",
    answer:
      "ทนายของสำนักงานขึ้นทะเบียนกับสภาทนายความในพระบรมราชูปถัมภ์ และผู้ทำคำรับรองลายมือชื่อและเอกสารขึ้นทะเบียนตามข้อบังคับว่าด้วยการขึ้นทะเบียนทนายความผู้ทำคำรับรองลายมือชื่อและเอกสาร ท่านสามารถดูรายชื่อ ประวัติ และภาพใบอนุญาตของทนายแต่ละท่านได้ในหน้าประวัติทีมงาน และขอตรวจสอบเลขที่ใบอนุญาตกับสภาทนายความได้",
  },
  {
    question: "ค่าบริการทางกฎหมายคิดอย่างไร ทำไมไม่ประกาศราคาบนเว็บไซต์?",
    answer:
      "ค่าบริการขึ้นกับลักษณะงานจริง เช่น ทุนทรัพย์ ศาลที่มีเขตอำนาจ จำนวนนัด ความซับซ้อนของเอกสาร ภาษาที่ต้องแปล และเส้นทางรับรองต่อ จึงไม่มีราคาเดียวที่ใช้ได้กับทุกเรื่อง ทีมงานจะประเมินเรื่องเบื้องต้นและแจ้งค่าบริการเป็นลายลักษณ์อักษรก่อนเริ่มงานทุกครั้ง ส่วนค่าธรรมเนียมศาลและค่าธรรมเนียมราชการเป็นอัตราของหน่วยงานและเรียกเก็บตามจริง",
  },
  {
    question: "สำนักงานบอกผลคดีหรือผลการพิจารณาของหน่วยงานล่วงหน้าได้หรือไม่?",
    answer:
      "ไม่ได้ และไม่ควรมีสำนักงานใดรับประกัน เพราะผลคดีเป็นอำนาจของศาล และการรับเอกสารเป็นดุลพินิจของหน่วยงานผู้รับ สิ่งที่สำนักงานทำได้คืออธิบายความเป็นไปได้ตามข้อเท็จจริงและข้อกฎหมาย เตรียมพยานหลักฐานและเอกสารให้ครบตามรูปแบบที่ปลายทางกำหนด และแจ้งความเสี่ยงให้ทราบก่อนตัดสินใจ",
  },
  {
    question: "Notary Public ในประเทศไทยคืออะไร และทำไมต้องใช้ทนายผู้ได้รับใบอนุญาต?",
    answer:
      "Notarial Services Attorney คือทนายความที่ขึ้นทะเบียนกับสภาทนายความในพระบรมราชูปถัมภ์ตามข้อบังคับที่เกี่ยวข้อง เพื่อทำคำรับรองลายมือชื่อและเอกสาร การรับรองที่ต้องใช้จริงขึ้นกับประเภทเอกสาร ประเทศ และหน่วยงานผู้รับ จึงควรตรวจข้อกำหนดกับหน่วยงานปลายทางก่อนดำเนินการทุกครั้ง",
  },
  {
    question: "ค่าบริการรับรองเอกสาร Notary Public คิดอย่างไร และสอบถามได้ทางไหน?",
    answer:
      "ค่าบริการคิดตามลักษณะงานจริง เช่น ประเภทเอกสาร จำนวนฉบับ จำนวนหน้าที่ต้องแปล ความเร่งด่วน และเส้นทางรับรองต่อ (กรมการกงสุล/สถานทูต) จึงไม่ประกาศราคาเดียวบนเว็บ กรุณาส่งภาพเอกสารและชื่อหน่วยงานปลายทางมาทาง โทร 094-895-8999 · LINE @Thainotary หรืออีเมล เจ้าหน้าที่จะแจ้งค่าบริการและกำหนดเสร็จเป็นลายลักษณ์อักษรก่อนเริ่มงานทุกครั้ง ส่วนค่าธรรมเนียมราชการเป็นอัตราของหน่วยงานและเรียกเก็บตามจริง",
  },
  {
    question: "ให้บริการครอบคลุมพื้นที่ใดบ้าง?",
    answer:
      "สำนักงานใหญ่ตั้งอยู่ที่วังทองหลาง กรุงเทพฯ และมีข้อมูลสำนักงานในขอนแก่น อุดรธานี และหนองคาย ลูกค้าต่างจังหวัดหรือต่างประเทศสามารถส่งภาพเอกสารให้เจ้าหน้าที่ประเมินขั้นต้นได้ แต่กรณีที่ต้องลงลายมือชื่อต่อหน้าทนาย เจ้าหน้าที่จะแจ้งวิธีนัดหมายตามประเภทเอกสารและข้อกำหนดของหน่วยงานผู้รับ",
  },
  {
    question: "ใช้เวลานานเท่าใดในการรับรองเอกสาร?",
    answer:
      "ระยะเวลาดำเนินการขึ้นกับประเภทเอกสาร จำนวนฉบับ การตรวจต้นฉบับ การแปล และการรับรองต่อกับกรมการกงสุลหรือสถานทูต กรุณาส่งภาพเอกสารและระบุประเทศหรือหน่วยงานปลายทางเพื่อให้เจ้าหน้าที่ตรวจและยืนยันกรอบเวลาก่อนเริ่มงาน",
  },
];

const HERO_SIZES = "(min-width: 1024px) 480px, 92vw";

const TITLE = "สำนักงานกฎหมายครบวงจร กรุงเทพ Notary — Thai Notary Law";
const DESC =
  "สำนักงานกฎหมายในกรุงเทพ ประเทศไทย โดยทนายผู้ได้รับใบอนุญาต • คดีความ บริษัท สัญญา ครอบครัว มรดก • Notary Bangkok แปล กงสุล • โทร 094-895-8999";

export const Route = createFileRoute("/")({
  component: Home,
  loader: () => ({ h: (() => {
    const ogImage = absoluteAsset(teamAsset.url);
    return {
      meta: [
        { title: ctrTitle(TITLE) },
        { name: "description", content: ctrDesc(DESC) },
        { property: "og:title", content: ctrTitle(TITLE) },
        { property: "og:description", content: ctrDesc(DESC) },
        { property: "og:url", content: absoluteUrl("/", null, "th") },
        { property: "og:type", content: "website" },
        { property: "og:image", content: ogImage },
        { property: "og:image:width", content: "1200" },
        { property: "og:image:height", content: "630" },
        { property: "og:image:alt", content: "ทีมทนายของสำนักงาน — Thai Notary Law & Service" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:image", content: ogImage },
      ],
      links: [
        { rel: "canonical", href: absoluteUrl("/", null, "th") },
        ...canonicalAndHreflang("/", null, "th").filter((l) => l.rel === "alternate"),
        // The hero is the page's only LCP candidate; React auto-emits the
        // matching responsive <link rel="preload"> for the eager hero <img>.
      ],

      scripts: [
        jsonLdScript(
          buildJsonLd([
            webPageSchema({ url: "/", title: TITLE, description: DESC }),
            legalServiceSchema({
              name: SITE.legalName,
              description:
                "สำนักงานกฎหมายครบวงจรในประเทศไทย รับงานคดีความและว่าความทุกศาล งานบริษัทและสัญญา ครอบครัว มรดก ที่ดิน แรงงาน ภาษี ทรัพย์สินทางปัญญา PDPA และงานรับรองเอกสารเพื่อใช้ในต่างประเทศ",
              url: "/",
              areaServed: "TH",
              serviceType: "Law Firm",
            }),
            howToSchema({
              name: "ขั้นตอนการทำงานกับสำนักงานกฎหมาย",
              description:
                "ลำดับการทำงานตั้งแต่ประเมินเรื่องเบื้องต้น เสนอแนวทางและค่าบริการ ตั้งเรื่อง ดำเนินการแทนลูกความ จนถึงรายงานผล",
              steps: FIRM_STEPS.map((s) => ({ name: s.title, text: s.body })),
            }),
            faqPageSchema(HOME_FAQ),
          ]),
        ),
      ],
    };
  })() }),
  head: ({ loaderData }) => loaderData?.h ?? {},
});

/** International-document work — kept intact so existing Notary/translation SEO holds. */
const INTERNATIONAL = [
  { name: "รับรองลายมือชื่อ", note: "ประเมินเอกสารก่อน", desc: "Signature Certification สำหรับสัญญาและเอกสารทั่วไป" },
  { name: "รับรองสำเนาเอกสาร", note: "ประเมินเอกสารก่อน", desc: "Certified True Copy — Passport, ทะเบียนบ้าน, ปริญญาบัตร" },
  { name: "Affidavit / คำสาบาน", note: "ตรวจรูปแบบปลายทาง", desc: "รับรองคำให้การเพื่อยื่นศาลหรือหน่วยงานต่างประเทศ" },
  { name: "หนังสือมอบอำนาจ", note: "ตรวจรูปแบบปลายทาง", desc: "Power of Attorney สำหรับธุรกรรมข้ามประเทศ" },
  { name: "แปลเอกสารรับรอง", note: "สอบถามเจ้าหน้าที่", desc: "แปลไทย-อังกฤษ-จีน-ญี่ปุ่น-เกาหลี พร้อมการรับรองตามขอบเขตงาน" },
  { name: "รับรองกงสุล/สถานทูต", note: "ตรวจลำดับการรับรอง", desc: "ประสานการยื่นกระทรวงการต่างประเทศและสถานทูตตามข้อกำหนดปลายทาง" },
];

function Home() {
  return (
    <>
      <FirmHero
        image={firmHeroTeam}
        sizes={HERO_SIZES}
        alt="ทนายความอธิบายเอกสารราชการไทยและหนังสือเดินทางให้ลูกค้าที่โต๊ะประชุมในสำนักงาน"
        width={1536}
        height={1152}
      />

      <TrustBar />

      <section aria-labelledby="home-answer" className="border-b border-border/60 bg-background">
        <div className="mx-auto max-w-5xl px-6 py-14">
          <h2 id="home-answer" className="text-2xl font-bold text-foreground md:text-3xl">
            ทุกเรื่องเริ่มจากการวางลำดับให้ถูกต้อง
          </h2>
          <p data-speakable className="answer-summary mt-4 leading-relaxed text-muted-foreground">
            หลายเรื่องแก้ได้ก่อนถึงชั้นศาล เช่น แก้สัญญาให้รัดกุม ทำหนังสือบอกกล่าว หรือเจรจาไกล่เกลี่ย
            ขณะที่บางเรื่องมีอายุความและกำหนดเวลาที่พลาดไม่ได้ ส่วนเอกสารที่จะใช้ในต่างประเทศ
            บางฉบับต้องเพียงคำรับรองโดยทนาย บางฉบับต้องรับรองต่อกับกรมการกงสุลและสถานทูต
            ทีมงานจะตรวจลำดับที่ปลายทางกำหนดก่อน เพื่อไม่ให้ท่านเสียเวลาและค่าธรรมเนียมในขั้นที่ไม่จำเป็น
          </p>
          <nav aria-label="สายงานที่เกี่ยวข้อง" className="mt-5 flex flex-wrap gap-x-5 gap-y-3 text-sm font-semibold">
            <Link to="/practice" className="text-primary hover:underline">สายงานกฎหมายทั้งหมด</Link>
            <Link to="/litigation" className="text-primary hover:underline">คดีความและว่าความ</Link>
            <Link to="/corporate" className="text-primary hover:underline">ธุรกิจและบริษัท</Link>
            <Link to="/notary" className="text-primary hover:underline">บริการ Notary Public</Link>
            <Link to="/translation" className="text-primary hover:underline">บริการแปลเอกสาร</Link>
            <Link to="/lgz" className="text-primary hover:underline">รับรองกงสุลและสถานทูต</Link>
            <Link to="/contact" className="text-primary hover:underline">ส่งเรื่องให้ทนายตรวจเบื้องต้น</Link>
          </nav>
        </div>
      </section>

      <PracticeAreaGrid />
      <OneStopJourney />

      <section aria-labelledby="international" className="border-b border-border/60 bg-muted/40">
        <div className="mx-auto max-w-6xl px-6 py-16">
          <div className="mb-10 flex items-end justify-between gap-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.24em] text-[var(--gold)]">
                งานเอกสารระหว่างประเทศ
              </p>
              <h2 id="international" className="mt-2 text-3xl font-bold tracking-tight text-foreground md:text-4xl">
                Notary Public • แปลรับรอง • กงสุลและสถานทูต
              </h2>
              <p className="mt-4 max-w-3xl leading-relaxed text-muted-foreground">
                งานส่วนนี้เป็นสายงานที่สำนักงานทำต่อเนื่องมากกว่า 15 ปี และมักต่อเนื่องกับงานคดีหรืองานบริษัท
                เช่น หนังสือมอบอำนาจเพื่อใช้ในคดีต่างประเทศ หรือเอกสารบริษัทเพื่อจดทะเบียนในต่างประเทศ
              </p>
            </div>
            <Link
              to="/services"
              className="hidden text-sm font-semibold text-primary hover:underline md:inline"
            >
              ดูทั้งหมด →
            </Link>
          </div>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {INTERNATIONAL.map((s) => (
              <article
                key={s.name}
                className="rounded-xl border border-border bg-card p-6 transition-all hover:border-[var(--gold)] hover:shadow-lg"
              >
                <div className="flex items-baseline justify-between gap-3">
                  <h3 className="text-base font-semibold text-foreground">{s.name}</h3>
                  <span className="text-sm font-bold text-[var(--gold)]">{s.note}</span>
                </div>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.desc}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <ProcessSteps />
      <WhyLawFirm />
      <AttorneyStrip />

      <GlobalImageStrip variant="home" />

      <section aria-labelledby="faq" className="mx-auto max-w-4xl px-6 py-20">
        <p className="text-xs font-semibold uppercase tracking-[0.24em] text-[var(--gold)]">คำถามที่พบบ่อย</p>
        <h2 id="faq" className="mt-2 text-3xl font-bold tracking-tight text-foreground md:text-4xl">
          คำถามที่พบบ่อยเกี่ยวกับสำนักงานและบริการ
        </h2>
        <dl className="mt-10 space-y-6">
          {HOME_FAQ.map((f) => (
            <div key={f.question} className="rounded-xl border border-border bg-card p-6">
              <dt className="text-base font-semibold text-foreground">{f.question}</dt>
              <dd className="mt-3 text-sm leading-relaxed text-muted-foreground">{f.answer}</dd>
            </div>
          ))}
        </dl>
      </section>

      <div className="mx-auto max-w-4xl px-6 pb-20">
        <OfficialServiceSources theme="law" />
        <OfficialServiceSources theme="notary" />
        <p className="mt-6 text-xs text-muted-foreground">
          ปรับปรุงเนื้อหาล่าสุด: 7 ตุลาคม 2569 · ข้อมูลในเว็บไซต์เป็นความรู้ทั่วไป โปรดตรวจเงื่อนไขล่าสุดกับหน่วยงานที่เกี่ยวข้องก่อนดำเนินการ
        </p>
      </div>

      <FirmCta />
    </>
  );
}
